import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {twMerge} from '../../../common/twMerge';
import {TextareaV2Props} from './TextareaV2Props.interface';

export const TextareaV2 = forwardRef<HTMLTextAreaElement, TextareaV2Props>(
  (
    {
      value: _value = '',
      onChange: _onChange,
      maxLength,
      className,
      applyAutoHeight,
      ...props
    },
    ref
  ) => {
    const innerRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(innerRef.current);
      } else {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current =
          innerRef.current;
      }
    }, [ref]);

    // 초기 height값을 잡아두고, 이를 초기화값으로 사용합니다.
    const [initialHeightPx, setInitialHeightPx] = useState(0);
    useEffect(() => {
      if (innerRef.current) {
        setInitialHeightPx(innerRef.current.clientHeight);
      }
    }, []);

    const value = useMemo(() => {
      return String(_value).slice(0, maxLength);
    }, [maxLength, _value]);

    useEffect(() => {
      if (!innerRef.current || !initialHeightPx) return;

      innerRef.current.style.height = `${initialHeightPx}px`;
      if (applyAutoHeight) {
        innerRef.current.style.height = `${innerRef.current.scrollHeight}px`;
      }
    }, [applyAutoHeight, initialHeightPx, value]);

    const onChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        // e.target.value에는 이번에 입력한 텍스트가 포함되어 들어가는데 이 수치가 maxLength를 넘어선 수치라면 기존 value를 그대로 사용하도록 처리합니다.
        if (maxLength && e.target.value.length > maxLength) {
          e.target.value = value;
        }
        _onChange?.(e);
      },
      [maxLength, _onChange, value]
    );

    return (
      <textarea
        ref={innerRef}
        onChange={onChange}
        // value가 비어있을 때의 높이를 기본값으로 잡기 위해 잠깐 빈 값을 넣어줍니다.
        value={initialHeightPx ? value : ''}
        className={twMerge(
          'font-medium2 w-full resize-none whitespace-pre-wrap break-all bg-transparent text-theme-solid-100 outline-hidden scrollbar-hide placeholder:text-theme-solid-40 disabled:text-theme-solid-20 disabled:placeholder:text-theme-solid-20 h-18px disabled:cursor-not-allowed min-w-0',
          'disabled:[-webkit-text-fill-color]:text-theme-solid-20 disabled:opacity-100',
          className
        )}
        {...props}
      />
    );
  }
);
